import { Fragment, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";

export default function EmployeeSalaryAssignment() {
  const [open, setOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);

  const [assignments, setAssignments] = useState([
    {
      employee: "E001 - John Doe",
      structure: "Below 30K - Male",
      effectiveFrom: "2025-01-01",
      effectiveTo: "2025-12-31",
      components: [
        { name: "Basic", amount: 12000 },
        { name: "HRA", amount: 6000 },
        { name: "Conveyance", amount: 1600 },
      ],
    },
    {
      employee: "E002 - Jane Smith",
      structure: "Below 30K - Female",
      effectiveFrom: "2025-04-01",
      effectiveTo: "2025-12-31",
      components: [
        { name: "Basic", amount: 11500 },
        { name: "HRA", amount: 5750 },
        { name: "Conveyance", amount: 1600 },
      ],
    },
  ]);

  // Structures available (would come from Pay Structure Master)
  const structures = [
    {
      name: "Below 30K - Male",
      components: [
        { name: "Basic", amount: 12000 },
        { name: "HRA", amount: 6000 },
        { name: "Conveyance", amount: 1600 },
      ],
    },
    {
      name: "Below 30K - Female",
      components: [
        { name: "Basic", amount: 11500 },
        { name: "HRA", amount: 5750 },
        { name: "Conveyance", amount: 1600 },
      ],
    },
    {
      name: "Above 30K",
      components: [
        { name: "Basic", amount: 18000 },
        { name: "HRA", amount: 9000 },
        { name: "Conveyance", amount: 2000 },
      ],
    },
  ];

  // Employees (would come from Employee Master)
  const employees = ["E001 - John Doe", "E002 - Jane Smith", "E003 - Rahul"];

  const [form, setForm] = useState({
    employee: "",
    structure: "",
    effectiveFrom: "",
    effectiveTo: "",
    components: [],
  });

  const handleSave = () => {
    if (!form.employee || !form.structure || !form.effectiveFrom) return; // basic guard

    if (editIndex !== null) {
      const updated = [...assignments];
      updated[editIndex] = form;
      setAssignments(updated);
    } else {
      setAssignments([...assignments, form]);
    }
    setForm({ employee: "", structure: "", effectiveFrom: "", effectiveTo: "", components: [] });
    setEditIndex(null);
    setOpen(false);
  };

  const handleEdit = (index) => {
    setForm(assignments[index]);
    setEditIndex(index);
    setOpen(true);
  };

  const handleDelete = (index) => {
    setAssignments(assignments.filter((_, i) => i !== index));
  };

  const handlePreview = (index) => {
    setPreviewIndex(index);
    setPreviewOpen(true);
  };

  const handleStructureChange = (structureName) => {
    const structure = structures.find((s) => s.name === structureName);
    setForm({
      ...form,
      structure: structureName,
      components: structure ? structure.components.map((c) => ({ ...c })) : [],
    });
  };

  const updateComponentAmount = (index, value) => {
    const num = Number(value || 0);
    const updated = [...form.components];
    updated[index].amount = num;
    setForm({ ...form, components: updated });
  };

  const previewData = useMemo(
    () => (previewIndex != null ? assignments[previewIndex] : null),
    [previewIndex, assignments]
  );

  const previewTotals = useMemo(() => {
    if (!previewData) return { monthly: 0, annual: 0 };
    const monthly = (previewData.components || []).reduce(
      (sum, c) => sum + Number(c.amount || 0),
      0
    );
    return { monthly, annual: monthly * 12 };
  }, [previewData]);

  return (
    <div className="bg-white dark:bg-gray-900 p-4 font-poppins rounded-2xl">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Employee Salary Assignment</h2>
        <button
          onClick={() => setOpen(true)}
          className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700"
        >
          + Assign Salary
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg font-poppins">
        <table className="min-w-full table-auto md:table-fixed text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
            <tr>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Employee</th>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Pay Structure</th>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Effective From</th>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Effective To</th>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Components</th>
              <th className="py-2 px-4 text-center  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {assignments.map((a, i) => (
              <tr key={i} className="border-b dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-950 dark:hover:text-white">
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">{a.employee}</td>
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">{a.structure}</td>
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">{a.effectiveFrom}</td>
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">{a.effectiveTo || "-"}</td>
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">
                  {(a.components || []).map((c, idx) => (
                    <div key={idx}>
                      {c.name}: {Number(c.amount || 0).toLocaleString()}
                    </div>
                  ))}
                </td>
                <td className="py-2 px-4 text-xs dark:text-white text-center truncate md:text-sm whitespace-nowrap">
                  <button
                    onClick={() => handlePreview(i)}
                    className="px-2 py-1 text-blue-500 hover:underline hover:underline-offset-4 rounded"
                  >
                    Preview
                  </button>
                  <button
                    onClick={() => handleEdit(i)}
                    className="px-2 py-1 text-yellow-500 hover:underline hover:underline-offset-4 rounded"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(i)}
                    className="px-2 py-1 text-red-500 hover:underline hover:underline-offset-4 rounded"
                  >
                    Del
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Transition appear show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setOpen(false)}>
          <div className="fixed inset-0 flex items-center justify-center p-4 bg-black/25">
            <Dialog.Panel className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-lg">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                {editIndex !== null ? "Edit Salary Assignment" : "New Salary Assignment"}
              </Dialog.Title>

              <div className="mt-4 space-y-3">
                {/* Employee */}
                <select
                  value={form.employee}
                  onChange={(e) => setForm({ ...form, employee: e.target.value })}
                  className="w-full border p-2 rounded"
                >
                  <option value="">Select Employee</option>
                  {employees.map((emp, idx) => (
                    <option key={idx} value={emp}>
                      {emp}
                    </option>
                  ))}
                </select>

                {/* Pay Structure */}
                <select
                  value={form.structure}
                  onChange={(e) => handleStructureChange(e.target.value)}
                  className="w-full border p-2 rounded"
                >
                  <option value="">Select Pay Structure</option>
                  {structures.map((s, idx) => (
                    <option key={idx} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>

                {/* Dates */}
                <div className="flex gap-3">
                  <input
                    type="date"
                    value={form.effectiveFrom}
                    onChange={(e) =>
                      setForm({ ...form, effectiveFrom: e.target.value })
                    }
                    className="w-full border p-2 rounded"
                  />
                  <input
                    type="date"
                    value={form.effectiveTo}
                    onChange={(e) =>
                      setForm({ ...form, effectiveTo: e.target.value })
                    }
                    className="w-full border p-2 rounded"
                  />
                </div>

                {/* Components */}
                <div className="border p-3 rounded">
                  <h3 className="font-semibold mb-2">Components</h3>
                  {form.components.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      Select a structure to load components
                    </p>
                  ) : (
                    form.components.map((c, idx) => (
                      <div key={idx} className="flex gap-3 items-center mb-2">
                        <span className="w-40">{c.name}</span>
                        <input
                          type="number"
                          value={c.amount}
                          onChange={(e) => updateComponentAmount(idx, e.target.value)}
                          className="w-full border p-2 rounded"
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 rounded-md border bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 rounded-md bg-blue-600 text-white"
                >
                  Save
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>

      {/* Preview Modal (read-only) */}
      <Transition appear show={previewOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setPreviewOpen(false)}>
          <div className="fixed inset-0 flex items-center justify-center p-4 bg-black/25">
            <Dialog.Panel className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-lg">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Assignment Preview
              </Dialog.Title>

              {previewData ? (
                <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="font-semibold">Employee:</span> {previewData.employee}</div>
                    <div><span className="font-semibold">Structure:</span> {previewData.structure}</div>
                    <div><span className="font-semibold">Effective From:</span> {previewData.effectiveFrom}</div>
                    <div><span className="font-semibold">Effective To:</span> {previewData.effectiveTo || "-"}</div>
                  </div>

                  <div className="border rounded">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 border">Component</th>
                          <th className="px-4 py-2 border">Monthly Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(previewData.components || []).map((c, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-2 border">{c.name}</td>
                            <td className="px-4 py-2 border">
                              {Number(c.amount || 0).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-gray-50 font-semibold">
                          <td className="px-4 py-2 border">Total (Monthly)</td>
                          <td className="px-4 py-2 border">
                            {previewTotals.monthly.toLocaleString()}
                          </td>
                        </tr>
                        <tr className="bg-gray-50 font-semibold">
                          <td className="px-4 py-2 border">Total (Annual)</td>
                          <td className="px-4 py-2 border">
                            {previewTotals.annual.toLocaleString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setPreviewOpen(false)}
                  className="px-4 py-2 rounded-md bg-blue-600 text-white"
                >
                  Close
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
